import { useState, useRef, useCallback, useEffect } from 'react';
import { Box, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { NewPageTitleBar, AppCard, EditorSidebar } from '@/components';
import { getMenuData } from '@core/data/menuData';
import {
    RfidTagFilterEditor,
    RfidTagFileImporter,
    RfidTagFileImporterEditor,
    RfidTagFiltersTable,
    RfidTagsTable,
    RfidTagProductEditor,
    RfidTagFileImporterRef
} from './components/rfidtags';
import { eventBus, EventTypes } from '@core/pubsub/EventBus.ts';
import { RfidTagFilter, RfidTagFileImportData } from './components/rfidtags/types.ts';
import { FCSecurePage, useRegisterRefresh } from '@/components/shared/navigation/securePage.ts';
import { Product, ProductType } from '@core/store/slices/apiSlice.ts';
import { DateTime } from 'luxon';

export interface RfidTagsTableRef {
    load: () => void;
}
export interface RfidTagFiltersTableRef {
    load: () => void;
}

const RfidTags: FCSecurePage = () => {
    const { t } = useTranslation();
    const [selected, setSelected] = useState<any>(null);
    const [selectedKind, setSelectedKind] = useState<string>('');

    const [productTypes] = useState<ProductType[]>([]);
    const [lastUpdate, setLastUpdate] = useState<DateTime | undefined>(undefined);

    const fileImporterRef = useRef<RfidTagFileImporterRef>(null);
    const rfidTagsTableRef = useRef<RfidTagsTableRef>(null);
    const rfidTagFiltersTableRef = useRef<RfidTagFiltersTableRef>(null);

    useEffect(() => {
        setLastUpdate(DateTime.now());
    }, []);

    const deselectLoad = useCallback(() => {
        fileImporterRef?.current?.resetValidation();
        // Close the editor
        setSelected(null);
        setSelectedKind('');
    }, []);

    const deselect = useCallback(() => {
        setSelected(null);
        setSelectedKind('');
    }, []);

    const editRfidTagFilter = useCallback((item: RfidTagFilter) => {
        setSelected(item);
        setSelectedKind('RfidTagFilter');
    }, []);

    const addRfidTagFilter = useCallback(() => {
        setSelected({ id: null, filter: '' });
        setSelectedKind('RfidTagFilter');
    }, []);

    const importRfidTagFile = useCallback((data: RfidTagFileImportData) => {
        setSelected(data);
        setSelectedKind('RfidTagFileImport');
    }, []);

    const addProduct = useCallback((product: Product) => {
        setSelected(product);
        setSelectedKind('RfidTagProduct');
    }, []);

    const handleRefresh = useCallback(async () => {
        eventBus.emit(EventTypes.WIDGETS_RELOAD);
        setLastUpdate(DateTime.now());
    }, []);

    useRegisterRefresh(handleRefresh);

    const handleProductSaved = useCallback(
        (product: Product) => {
            // Reload the file importer to get the new product list
            rfidTagsTableRef.current?.load();
            fileImporterRef.current?.load();
            // Close the editor
            deselectLoad();
        },
        [deselectLoad]
    );

    const handleFilterSaved = useCallback(() => {
        rfidTagFiltersTableRef.current?.load();
        // Close the editor
        deselectLoad();
    }, [deselectLoad]);

    const handleFileImporterSaved = useCallback(() => {
        fileImporterRef.current?.load();
        // Close the editor
        deselectLoad();
    }, [deselectLoad]);

    return (
        <Box>
            <NewPageTitleBar
                icon={<i className="zmdi zmdi-settings" />}
                title={t('rfidTags')}
                menus={getMenuData()}
                iconSize="20px"
                currentRoute={{ path: '/manage-organisation/rfidtags' }}
                t={t}
                lastUpdate={lastUpdate}
            />

            <Box sx={{ width: '100%', mt: 1, px: 3 }}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6} key="left-column">
                        <AppCard icon="filter_alt" heading={t('rfidTagFilters')}>
                            <RfidTagFiltersTable
                                ref={rfidTagFiltersTableRef}
                                onEdit={editRfidTagFilter}
                                onAdd={addRfidTagFilter}
                            />
                        </AppCard>

                        <Box sx={{ mt: 3, pb: 3 }}>
                            <AppCard icon="upload" heading={t('importRfidTags')}>
                                <RfidTagFileImporter
                                    ref={fileImporterRef}
                                    onImportRfidTagFile={importRfidTagFile}
                                    onCancelImporterEditor={deselect}
                                    onAddProduct={addProduct}
                                />
                            </AppCard>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={6} key="rfid-tags-grid">
                        <AppCard icon="leak_add" heading={t('rfidTags')}>
                            <RfidTagsTable ref={rfidTagsTableRef} key="rfid-tags-table" />
                        </AppCard>
                    </Grid>
                </Grid>
            </Box>

            <EditorSidebar open={!!selected} onClose={deselect}>
                {selected && selectedKind === 'RfidTagFilter' && (
                    <RfidTagFilterEditor item={selected} onCancel={deselect} onChanged={handleFilterSaved} />
                )}
                {selected && selectedKind === 'RfidTagFileImport' && (
                    <RfidTagFileImporterEditor
                        item={selected}
                        onCancel={deselect}
                        onChanged={handleFileImporterSaved}
                    />
                )}
                {selected && selectedKind === 'RfidTagProduct' && (
                    <RfidTagProductEditor
                        item={selected}
                        itemTypes={productTypes}
                        onCancel={deselect}
                        onSave={handleProductSaved}
                    />
                )}
            </EditorSidebar>
        </Box>
    );
};

export default RfidTags;
